import * as React from 'react';
import { Button, Badge } from 'reactstrap';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { State } from '../redux/state';
import { userApiBasePath } from '../redux/selectors/userApi';
import * as selectors from '../redux/selectors';
import { LProps, withLocalization, withRouterAndHelpers, RouterProps } from '../utils/wrappers';
import { Routes, makeSimpleRoutePath } from '../utils/routerHelper';
import LinkButton from './LinkButton';
import {
  NormalizedNotification,
  normalizeNotification,
  resolveNotificationPath,
  isNotificationUnread,
  notificationActionLabel,
  filterNotificationsByCategory,
  isParticipantNotification
} from '../utils/notificationUtils';
import './notificationBell.scss';

interface Props {
  apiBase: string;
  token: string;
  competitionName: string;
  isLoggedIn: boolean;
  isModerator: boolean;
}

interface LocalState {
  open: boolean;
  unreadCount: number;
  items: NormalizedNotification[];
  loading: boolean;
}

class NotificationBell extends React.Component<LProps<RouterProps<{}> & Props>, LocalState> {
  public state: LocalState = {
    open: false,
    unreadCount: 0,
    items: [],
    loading: false
  };
  private pollTimer: number | null = null;
  private rootRef: HTMLDivElement | null = null;

  public componentDidMount() {
    if (this.props.isLoggedIn && this.props.token) {
      this.refreshUnread();
      this.pollTimer = window.setInterval(this.refreshUnread, 60000);
    }
    document.addEventListener('mousedown', this.handleDocumentMouseDown);
  }

  public componentWillUnmount() {
    if (this.pollTimer !== null) {
      window.clearInterval(this.pollTimer);
    }
    document.removeEventListener('mousedown', this.handleDocumentMouseDown);
  }

  private handleDocumentMouseDown = (event: MouseEvent) => {
    if (!this.state.open || !this.rootRef) return;
    if (event.target instanceof Node && !this.rootRef.contains(event.target)) {
      this.setState({ open: false });
    }
  }

  private authHeaders = () => ({
    'Authorization': this.props.token,
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  })

  private refreshUnread = async () => {
    const { apiBase, competitionName, token } = this.props;
    if (!token || !competitionName) return;
    try {
      const res = await fetch(
        `${apiBase}/competitions/${encodeURIComponent(competitionName)}/notifications/unread_count`,
        { headers: this.authHeaders() }
      );
      if (!res.ok) return;
      const data = await res.json();
      this.setState({ unreadCount: data.unreadCount || 0 });
    } catch (_err) {
      // Badge is best-effort; ignore network blips.
    }
  }

  private loadPanel = async () => {
    const { apiBase, competitionName } = this.props;
    this.setState({ loading: true });
    try {
      const res = await fetch(
        `${apiBase}/competitions/${encodeURIComponent(competitionName)}/notifications?pageSize=20`,
        { headers: this.authHeaders() }
      );
      if (!res.ok) {
        this.setState({ loading: false });
        return;
      }
      const data = await res.json();
      const items = (data.notifications || []).map((raw: Record<string, unknown>) => normalizeNotification(raw));
      this.setState({
        items,
        unreadCount: data.unreadCount || 0,
        loading: false
      });
    } catch (_err) {
      this.setState({ loading: false });
    }
  }

  private toggle = () => {
    const next = !this.state.open;
    this.setState({ open: next });
    if (next) {
      this.loadPanel();
    }
  }

  private markAllRead = async () => {
    const { apiBase, competitionName } = this.props;
    await fetch(
      `${apiBase}/competitions/${encodeURIComponent(competitionName)}/notifications/read_all`,
      { method: 'POST', headers: this.authHeaders() }
    );
    this.setState({
      unreadCount: 0,
      items: this.state.items.map((item) => ({ ...item, readAt: item.readAt || new Date().toISOString() }))
    });
  }

  private openItem = async (item: NormalizedNotification) => {
    const { apiBase, history, t } = this.props;
    if (isNotificationUnread(item)) {
      await fetch(`${apiBase}/notifications/${item.id}/read`, {
        method: 'POST',
        headers: this.authHeaders()
      });
      this.setState({
        unreadCount: Math.max(0, this.state.unreadCount - 1),
        items: this.state.items.map((n) =>
          n.id === item.id ? { ...n, readAt: new Date().toISOString() } : n
        )
      });
    }
    history.push(resolveNotificationPath(item, t));
  }

  private openAllNotifications = () => {
    const { history, t } = this.props;
    this.setState({ open: false });
    history.push(makeSimpleRoutePath(Routes.Notifications, t));
  }

  public render() {
    const { t, isLoggedIn } = this.props;
    if (!isLoggedIn) return null;
    const { open, unreadCount, items, loading } = this.state;
    const { isModerator } = this.props;
    const visibleItems = filterNotificationsByCategory(
      items.filter((item) => isParticipantNotification(item, isModerator)),
      'all',
      isModerator
    );
    return (
      <div
        id="cc__notification_bell"
        className="cc__notification_bell"
        ref={(el) => { this.rootRef = el; }}
      >
        <LinkButton
          id="cc__notification_bell_toggle"
          className="cc__notification_bell__toggle"
          aria-label={t('Notifications')}
          aria-expanded={open}
          aria-haspopup="true"
          onClick={this.toggle}
        >
          <span className="cc__notification_bell__label">{t('Alerts')}</span>
          {unreadCount > 0 &&
            <Badge
              color="danger"
              pill={true}
              className="cc__notification_bell__badge"
            >{unreadCount > 99 ? '99+' : unreadCount}</Badge>
          }
        </LinkButton>
        {open &&
          <div
            className="cc__notification_bell__panel"
            role="dialog"
            aria-label={t('Notifications')}
          >
            <div className="cc__notification_bell__header">
              <strong>{t('Notifications')}</strong>
              <Button color="link" size="sm" onClick={this.markAllRead}>{t('Mark all read')}</Button>
            </div>
            {loading && <div className="cc__notification_bell__empty">{t('Loading…')}</div>}
            {!loading && visibleItems.length === 0 &&
              <div className="cc__notification_bell__empty cc__lighttext">{t('No notifications yet.')}</div>
            }
            {!loading && visibleItems.map((item) =>
              <div
                key={item.id}
                className={isNotificationUnread(item)
                  ? 'cc__notification_bell__item cc__notification_bell__item--unread'
                  : 'cc__notification_bell__item'}
              >
                <button
                  type="button"
                  className="cc__notification_bell__item__main"
                  onClick={() => this.openItem(item)}
                >
                  <div className="cc__notification_bell__title">{item.title}</div>
                  <div className="cc__notification_bell__body">{item.body}</div>
                </button>
                <Button color="link" size="sm" className="cc__notification_bell__item__link" onClick={() => this.openItem(item)}>
                  {notificationActionLabel(item, t)}
                </Button>
              </div>
            )}
            {!loading &&
              <div className="cc__notification_bell__footer">
                <Button color="link" size="sm" block={true} onClick={this.openAllNotifications}>
                  {t('View all notifications')}
                </Button>
              </div>
            }
          </div>
        }
      </div>
    );
  }
}

const mapStateToProps = (state: State): Props => ({
  apiBase: userApiBasePath(state),
  token: state.token || '',
  competitionName: (state.competition && state.competition.name) || selectors.competitionName(state) || '',
  isLoggedIn: selectors.isLoggedIn(state),
  isModerator: selectors.canViewCommsTools(state)
});

export default compose(
  withLocalization('navigation'),
  withRouterAndHelpers,
  connect(mapStateToProps)
)(NotificationBell) as React.ComponentClass;
